<template>
  <PageSection :title="t('profile.settingsTitle')">
    <AccordionItem
      :text="t('profile.myInfo')"
      @click="handleOpenPanel(PanelType.INFO)"
    />
    <AccordionItem
      :text="t('profile.resetPassword')"
      @click="handleOpenPanel(PanelType.PASSWORD)"
    />
    <AccordionItem
      v-if="showCdaOptIn"
      :text="t('profile.communicationPreferences.menuItem')"
      @click="handleOpenPanel(PanelType.COMMUNICATION)"
    />

    <!-- Shared Sliding Panel -->
    <SlidingPanel :isPanelVisible="isPanelVisible" :onClose="handleClosePanel">
      <template v-if="currentPanel === PanelType.INFO">
        <EditInfoForm :editable="isEditable" />
      </template>
      <template v-else-if="currentPanel === PanelType.COMMUNICATION">
        <CommunicationPreferencesForm @close="handleClosePanel" />
      </template>
      <template v-else-if="currentPanel === PanelType.PASSWORD">
        <EditPasswordForm />
      </template>
    </SlidingPanel>
  </PageSection>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue'

  // Config
  import { ModalId } from '@/config/modalIds'

  // Components
  import PageSection from '@/components/UI/PageSection.vue'
  import AccordionItem from './AccordionItem.vue'
  import SlidingPanel from '@/components/UI/SlidingPanel.vue'
  import EditInfoForm from '@/components/Profile/EditInfoForm/EditInfoForm.vue'
  import EditPasswordForm from '@/components/Profile/EditPasswordForm/EditPasswordForm.vue'
  import CommunicationPreferencesForm from '@/components/Profile/CommunicationPreferencesForm.vue'
  import { isCdaOptInEnabledForUser } from '@/logic/contact/isCdaOptInEnabled'

  // Store
  import { useStore } from '@/store'

  // Translations
  import { useI18n } from 'vue-i18n'

  // Service
  import CSEService from '@/services/CSEService'

  const { t } = useI18n()

  // Enum for Panel Types
  enum PanelType {
    INFO = 'info',
    COMMUNICATION = 'communication',
    PASSWORD = 'password',
  }

  const store = useStore()

  const showCdaOptIn = computed(() =>
    isCdaOptInEnabledForUser(store.state.auth?.user?.iresa_id)
  )

  // Visibility state
  const isPanelVisible = computed(() => {
    const checkIsOpen = store.getters['modal/isOpen']
    return checkIsOpen(ModalId.EDIT_INFO_PANEL)
  })

  // Tracks which panel to show
  const currentPanel = ref<PanelType | null>(null)

  // Methods
  const handleOpenPanel = (panel: PanelType): void => {
    currentPanel.value = panel
    store.commit('modal/open', ModalId.EDIT_INFO_PANEL)
  }

  const handleClosePanel = (): void => {
    currentPanel.value = null
    store.commit('modal/close', ModalId.EDIT_INFO_PANEL)
  }

  const isEditable = computed<boolean>(() => {
    const cseService = new CSEService()
    return !cseService.isCgosOrCGOSPM
  })
</script>
